import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Package, Mail } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { ordersAPI } from '../services/api';

const OrderSuccess = ({ cartCount, onClearCart }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const data = await ordersAPI.getCheckoutStatus(sessionId);
        setPaymentData(data);
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          // Clear the cart on successful payment
          if (onClearCart) {
            onClearCart();
          }
        } else if (data.status === 'expired') {
          setStatus('error');
        } else if (attempts < 5) {
          // Keep polling
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts, navigate, onClearCart]);

  return (
    <div className="min-h-screen bg-black">
      <Navbar cartCount={cartCount} />

      <div className="container mx-auto px-6 py-20">
        <div className="max-w-2xl mx-auto text-center">
          {status === 'loading' && (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12">
              <Loader2 className="w-16 h-16 text-amber-400 mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-serif font-bold text-white mb-4">
                Processing Your Payment
              </h1>
              <p className="text-gray-400">
                Please wait while we confirm your payment...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <h1 className="text-4xl font-serif font-bold text-white mb-4">
                Thank You for Your Order!
              </h1>
              <p className="text-xl text-amber-400 mb-6">
                Your payment was successful
              </p>
              
              <div className="bg-gray-800 rounded-lg p-6 mb-8 text-left">
                <div className="flex items-start gap-4 mb-4">
                  <Mail className="w-6 h-6 text-amber-400 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white">Confirmation Email Sent</h3>
                    <p className="text-sm text-gray-400">
                      We've sent an order confirmation to your email address with all the details.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Package className="w-6 h-6 text-amber-400 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white">What's Next?</h3>
                    <p className="text-sm text-gray-400">
                      We'll prepare your order with care and send you tracking information once it's shipped.
                    </p>
                  </div>
                </div>
              </div>

              {paymentData && (
                <div className="bg-amber-400/10 border border-amber-400/30 rounded-lg p-4 mb-8">
                  <p className="text-amber-400 font-semibold">
                    Amount Paid: £{(paymentData.amount_total / 100).toFixed(2)}
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/shop')}
                  className="bg-amber-400 hover:bg-amber-500 text-black font-semibold"
                >
                  Continue Shopping
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="border-amber-400 text-amber-400 hover:bg-amber-400/10"
                >
                  Back to Home
                </Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h1 className="text-3xl font-serif font-bold text-white mb-4">
                Payment Issue
              </h1>
              <p className="text-gray-400 mb-8">
                We couldn't confirm your payment. If you were charged, please contact us and we'll sort it out.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/checkout')}
                  className="bg-amber-400 hover:bg-amber-500 text-black font-semibold"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/contact')}
                  variant="outline"
                  className="border-amber-400 text-amber-400 hover:bg-amber-400/10"
                >
                  Contact Us
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OrderSuccess;
